/** SVG path data for editable shapes, and node accessors for node editing. */
import { type PathNode, flattenPath } from '../../engine/geometry/bezier';
import { FLATTEN_TOL, applyTransform, transformShape } from '../../engine/model/geometry';
import type { EmbObject, Shape } from '../../engine/model/types';
import type { Vec } from '../../engine/geometry/vec';

const cache = new WeakMap<EmbObject, { d: string; closed: boolean; outline: string | null }>();

function polyD(points: Vec[], closed: boolean): string {
  if (!points.length) return '';
  let d = `M${points[0].x.toFixed(3)} ${points[0].y.toFixed(3)}`;
  for (let i = 1; i < points.length; i++) d += `L${points[i].x.toFixed(3)} ${points[i].y.toFixed(3)}`;
  return closed ? d + 'Z' : d;
}

/** Path data in design coordinates. `closed` = fillable; `outline` = column body for columns. */
export function objectPath(o: EmbObject): { d: string; closed: boolean; outline: string | null } {
  const hit = cache.get(o);
  if (hit) return hit;
  const s = transformShape(o.shape, o.transform);
  let r: { d: string; closed: boolean; outline: string | null };
  switch (s.kind) {
    case 'area':
      r = { d: [s.outer, ...s.holes].map((ring) => polyD(flattenPath(ring, true, FLATTEN_TOL * 3), true)).join(''), closed: true, outline: null };
      break;
    case 'line':
      r = { d: polyD(flattenPath(s.nodes, s.closed, FLATTEN_TOL * 3), s.closed), closed: s.closed, outline: null };
      break;
    case 'column': {
      const a = flattenPath(s.railA, false, FLATTEN_TOL * 3);
      const b = flattenPath(s.railB, false, FLATTEN_TOL * 3);
      r = { d: polyD(a, false) + polyD(b, false), closed: false, outline: polyD([...a, ...b.slice().reverse()], true) };
      break;
    }
  }
  cache.set(o, r);
  return r;
}

export interface NodePathRef {
  key: string;
  nodes: PathNode[];
  closed: boolean;
}

/** All editable node lists of a shape. */
export function nodePaths(shape: Shape): NodePathRef[] {
  switch (shape.kind) {
    case 'area':
      return [{ key: 'outer', nodes: shape.outer, closed: true }, ...shape.holes.map((h, i) => ({ key: `hole:${i}`, nodes: h, closed: true }))];
    case 'line':
      return [{ key: 'nodes', nodes: shape.nodes, closed: shape.closed }];
    case 'column':
      return [{ key: 'railA', nodes: shape.railA, closed: false }, { key: 'railB', nodes: shape.railB, closed: false }];
  }
}

export function setNodePath(shape: Shape, key: string, nodes: PathNode[]): Shape {
  switch (shape.kind) {
    case 'area':
      if (key === 'outer') return { ...shape, outer: nodes };
      if (key.startsWith('hole:')) {
        const i = Number(key.slice(5));
        return { ...shape, holes: shape.holes.map((h, j) => (j === i ? nodes : h)) };
      }
      return shape;
    case 'line':
      return key === 'nodes' ? { ...shape, nodes } : shape;
    case 'column':
      return key === 'railA' ? { ...shape, railA: nodes } : key === 'railB' ? { ...shape, railB: nodes } : shape;
  }
}

export const minNodes = (closed: boolean): number => (closed ? 3 : 2);

/** Design-space position of a node and its handles. */
export function nodeInDesign(o: EmbObject, n: PathNode): { p: Vec; in?: Vec; out?: Vec } {
  return {
    p: applyTransform(n, o.transform),
    in: n.in ? applyTransform(n.in, o.transform) : undefined,
    out: n.out ? applyTransform(n.out, o.transform) : undefined,
  };
}

/** Index of the segment (start node) of a node path closest to a local point. */
export function nearestSegment(nodes: PathNode[], closed: boolean, p: Vec): number {
  let best = 0, bd = Infinity;
  const count = closed ? nodes.length : nodes.length - 1;
  for (let i = 0; i < count; i++) {
    const a = nodes[i], b = nodes[(i + 1) % nodes.length];
    const pts = flattenPath([a, b], false, 0.1);
    for (let k = 1; k < pts.length; k++) {
      const q = pts[k - 1], r = pts[k];
      const dx = r.x - q.x, dy = r.y - q.y;
      const l2 = dx * dx + dy * dy || 1;
      const t = Math.max(0, Math.min(1, ((p.x - q.x) * dx + (p.y - q.y) * dy) / l2));
      const d = Math.hypot(q.x + t * dx - p.x, q.y + t * dy - p.y);
      if (d < bd) {
        bd = d;
        best = i;
      }
    }
  }
  return best;
}
